import React, { createContext, useContext, useEffect, useRef, useState } from "react"
import { IIsBulkSelectionEnabledPropsModel } from "../../../../models"
import { useQuery, useQueryClient } from "react-query"
import { AxiosError } from "axios"
import { IHttpErrorResponseModel } from "@common-models/*"
import { CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR, CONST_VIVIER_STATUS_DISPONIBLE } from "@common-constants/*"
import { toast } from "react-toastify"
import { IGetViviersRequestModel, IGetViviersResponseItemModel, IGetViviersResponseModel, IItemInPreviewModel, IViviersListingContextPropsModel } from "./core/_models"
import { useAuth } from "../../../../AppContext"
import { getAnnoncesListingRequest } from "./core/_requests"
import ViviersListingFilters from "./_ViviersListingFilters"
import ViviersListingSearchBar from "./_ViviersListingSearchBar"
import ViviersListingList from "./_ViviersListingList"
import AnnoncesListingPreview from "./_ViviversListingPreview"
import useVivier from "../useVivier"

const REACT_QUERY_KEY_GET_LISTING = "REACT_QUERY_KEY_GET_VIVIERS_LISTING"
const SESSION_STORAGE_KEY_VIVIERS_FILTERS = "VIVIERS_LISTING_FILTERS"

const ViviersListingContext = createContext<IViviersListingContextPropsModel>({} as IViviersListingContextPropsModel)
export const useViviersListingContext = () => useContext(ViviersListingContext)

const ViviersListing = () => {
     const queryClient = useQueryClient()
     const { currentUser } = useAuth()
     const { archive: archiveViviers, restore: restoreViviers } = useVivier()

     // Referencing filters form, simple search bar form & preview container
     const filtersFormikRef = useRef<any>(null)
     const simpleSearchBarFormikRef = useRef<any>(null)
     const previewContainerRef = useRef<any>(null)

     // 1. Initialize filters from Session Storage or Default
     const [filters, setFilters] = useState<IGetViviersRequestModel>(() => {
          const savedFilters = sessionStorage.getItem(SESSION_STORAGE_KEY_VIVIERS_FILTERS)
          if (savedFilters) {
               try {
                    return JSON.parse(savedFilters)
               } catch (e) {
                    console.error("Error parsing saved viviers filters", e)
               }
          }
          return {
               responsables: currentUser?.teamRecrutement?.map(item => item.id as number),
               statuses: [CONST_VIVIER_STATUS_DISPONIBLE],
               page: 1,
               pageLength: 20,
          }
     })

     const [areFiltersVisible, setAreFiltersVisible] = useState<boolean>(false)
     const [itemInPreview, setItemInPreview] = useState<IItemInPreviewModel | null>(null)
     const [isBulkSelectionEnabled, setIsBulkSelectionEnabled] = useState<IIsBulkSelectionEnabledPropsModel>({
          enabled: false,
          checkedElementsInPage: [],
          areAllElementsInAllPagesChecked: false,
     })
     const [itemsBeingUpdated, setItemsBeingUpdated] = useState<IGetViviersResponseItemModel[]>([])

     // 2. Effect to save filters whenever they change
     useEffect(() => {
          sessionStorage.setItem(SESSION_STORAGE_KEY_VIVIERS_FILTERS, JSON.stringify(filters))
          // Note: We DO NOT call refetch() here. The useQuery dependency handles it.
     }, [filters])

     // 3. The Query
     // 🛑 CRITICAL FIX: The key must include 'filters'. This forces a refetch whenever filters changes.
     const listingQuery = useQuery<IGetViviersResponseModel, AxiosError>(
          [REACT_QUERY_KEY_GET_LISTING, filters],
          async () => {
               return getAnnoncesListingRequest(filters)
                    .then(r => {
                         // Stop loaders
                         if (filtersFormikRef.current) filtersFormikRef.current.setSubmitting(false)
                         if (simpleSearchBarFormikRef.current) simpleSearchBarFormikRef.current.setSubmitting(false)
                         return r.data
                    })
                    .catch((e: AxiosError) => {
                         const error: IHttpErrorResponseModel = e.response?.data
                         if (error?.code === CONST_HTTP_CUSTOM_CODE_FORM_VALIDATION_ERROR && error?.errors) {
                              for (const key in error.errors) filtersFormikRef.current?.setFieldError(key, error.errors[key])
                         }
                         if (filtersFormikRef.current) filtersFormikRef.current.setSubmitting(false)
                         if (simpleSearchBarFormikRef.current) simpleSearchBarFormikRef.current.setSubmitting(false)
                         toast.error(error?.detail, { autoClose: false })
                         throw e
                    })
          },
          {
               keepPreviousData: true, // Optional: Keeps list visible while loading new data
               refetchOnWindowFocus: false,
          }
     )

     function handleArchive(items: IGetViviersResponseItemModel[]) {
          archiveViviers(
               items.map(item => item.vivier),
               () => setItemsBeingUpdated(items),
               () => handleRefreshItemOfTheList(items).then(() => setItemInPreview(null)),
               () => resetBulkSelection(),
               () => resetBulkSelection()
          )
     }

     function handleRestore(items: IGetViviersResponseItemModel[]) {
          restoreViviers(
               items.map(item => item.vivier),
               () => setItemsBeingUpdated(items),
               () => handleRefreshItemOfTheList(items).then(() => setItemInPreview(null)),
               () => resetBulkSelection(),
               () => resetBulkSelection()
          )
     }

     function resetBulkSelection() {
          setIsBulkSelectionEnabled({
               enabled: false,
               checkedElementsInPage: [],
               areAllElementsInAllPagesChecked: false,
          })
          setItemsBeingUpdated([])
     }

     async function handleRefreshItemOfTheList(items: IGetViviersResponseItemModel[]) {
          const ids = items.map(item => item.vivier.id as number)
          try {
               const { data } = await getAnnoncesListingRequest({ ids, page: 1, pageLength: null })
               const not_returned_ids = ids.filter(id => !data.data.map(item => item.vivier.id).includes(id))
               if (not_returned_ids.length > 0) {
                    listingQuery.refetch().then()
               } else {
                    queryClient.setQueryData<IGetViviersResponseModel | undefined>(REACT_QUERY_KEY_GET_LISTING, (prev: IGetViviersResponseModel | undefined) => {
                         if (prev) {
                              prev.data = prev.data.map(item => {
                                   if (ids.includes(item.vivier.id as number)) {
                                        const updatedItem = data.data.find(updatedItem => updatedItem.vivier.id === item.vivier.id)
                                        if (updatedItem) return updatedItem
                                   }
                                   return item
                              })
                              const not_returned_ids = ids.filter(id => !data.data.map(item => item.vivier.id).includes(id))
                              prev.data = prev.data.filter(item => !not_returned_ids.includes(item.vivier.id as number))
                         }
                         return prev
                    })
               }
          } catch (e) {
               const error = (e as AxiosError).response?.data as IHttpErrorResponseModel
               toast.error(error?.detail, { autoClose: false })
          }
          resetBulkSelection()
     }

     return (
          <ViviersListingContext.Provider
               value={{
                    areFiltersVisible,
                    setAreFiltersVisible,
                    filters,
                    setFilters,
                    itemInPreview,
                    setItemInPreview,
                    REACT_QUERY_KEY_GET_LISTING,
                    listingQuery,
                    filtersFormikRef,
                    previewContainerRef,
                    simpleSearchBarFormikRef,
                    isBulkSelectionEnabled,
                    setIsBulkSelectionEnabled,
                    handleArchive,
                    handleRestore,
                    itemsBeingUpdated,
               }}
          >
               {window.innerWidth >= 1200 ? <ViewXlOrHigher /> : <ViewLgOrLess />}
          </ViviersListingContext.Provider>
     )
}

const ViewLgOrLess = () => {
     const context = useViviersListingContext()
     return (
          <>
               <div className={`${context.areFiltersVisible ? "d-none" : "mb-2"}`}>
                    <ViviersListingSearchBar />
               </div>
               {context.itemInPreview && context.itemInPreview.isPreviewVisible && (
                    <div className={"mb-2"}>
                         <AnnoncesListingPreview />
                    </div>
               )}
               <div className={`${!context.areFiltersVisible ? "d-none" : "mb-2"}`}>
                    <ViviersListingFilters />
               </div>
               <ViviersListingList />
          </>
     )
}

const ViewXlOrHigher = () => {
     const context = useViviersListingContext()
     return (
          <div className={"row"}>
               <div className={`col-5 ${!context.areFiltersVisible ? "d-none" : ""}`}>
                    <ViviersListingFilters />
               </div>
               <div className={`col ${context.itemInPreview && context.itemInPreview.isPreviewVisible ? "" : "col-7"}`}>
                    <div className={"row"}>
                         <div className={context.itemInPreview && context.itemInPreview.isPreviewVisible ? "col-5" : ""}>
                              <div className={`mb-2 ${context.areFiltersVisible ? "d-none" : ""}`}>
                                   <ViviersListingSearchBar />
                              </div>
                              <ViviersListingList />
                         </div>
                         {context.itemInPreview && context.itemInPreview.isPreviewVisible && (
                              <div className="col-7">
                                   <AnnoncesListingPreview />
                              </div>
                         )}
                    </div>
               </div>
          </div>
     )
}

export default ViviersListing
